import { useStore } from 'context/store'
import dayjs from 'dayjs'
import { observer } from 'mobx-react'
import { Button } from 'primereact/button'
import { Divider } from 'primereact/divider'
import { useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { DATE_TIME_FORMAT, FORMAT_TIME } from 'utils/constants/datetime'
import { useObjectSearchParams } from 'utils/hooks/useObjectSearchParams'
import EventInfo from './EventInfo'
import Question from './Question'
import RemarkInfo from './RemarkInfo'
import {
  EventApplicationUnitEnum,
  EventStatusEnum,
} from 'utils/constants/event'

const getEmptyCreateEventDefaults = () => ({
  title: '',
  description: '',
  location: '',
  timeslots: [
    {
      timeRange: {
        from: null,
        to: null,
      },
      maxRegistrations: null,
    },
  ],
  startTimeEvent: null,
  remindBefore: 24,
  endTime: null,
  receptionBefore: null,
  cancelBefore: null,
  qrValidUntil: null,
  applicationUnit: EventApplicationUnitEnum.Single,
  countdownAfter: 5,
  questions: [],
  remarksRequired: false,
  remarksLabel: '',
  remarksPlaceholder: '',
})

/**
 * Mock dev: slot 1 sau ~2h, slot 2 sau ~3h; cancelBefore ≤ bắt đầu slot 1 − 1h.
 */
const buildCreateEventDevMockDefaults = () => {
  const now = dayjs().second(0).millisecond(0)
  const slot1Start = now.add(1, 'hour').add(3, 'minute')
  const slot1End = slot1Start.add(15, 'minute')
  const slot2Start = now.add(3, 'hour')
  const slot2End = slot2Start.add(15, 'minute')
  const eventDay = slot1Start.startOf('day').toDate()
  const cancelBefore = slot1Start
    .subtract(1, 'hour')
    .subtract(2, 'minute')
    .toDate()
  const receptionBefore = now.add(15, 'minute').toDate()

  return {
    ...getEmptyCreateEventDefaults(),
    title: `Mock event ${new Date(Date.now()).toLocaleString()}`,
    description: '<p>Mock event  (dev).</p>',
    location: 'Mock progress location',
    startTimeEvent: eventDay,
    remindBefore: 0,
    receptionBefore,
    cancelBefore,
    timeslots: [
      {
        timeRange: {
          from: slot1Start.format(FORMAT_TIME),
          to: slot1End.format(FORMAT_TIME),
        },
        maxRegistrations: 10,
      },
      {
        timeRange: {
          from: slot2Start.format(FORMAT_TIME),
          to: slot2End.format(FORMAT_TIME),
        },
        maxRegistrations: 10,
      },
    ],
  }
}

const CreateEvent = () => {
  const { t } = useTranslation()

  const {
    eventManamentStore: {
      fetchDetail,
      selectedItem,
      loadingSubmit,
      setSelectedItem,
      update,
      create,
    },
  } = useStore()

  const { id } = useParams()
  const {
    searchObject: { cloneId },
  } = useObjectSearchParams()

  const defaultValues = useMemo(
    () =>
      process.env.NODE_ENV === 'development'
        ? buildCreateEventDevMockDefaults()
        : getEmptyCreateEventDefaults(),
    []
  )

  const form = useForm<any>({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  })

  const { handleSubmit, reset } = form

  useEffect(() => {
    reset(selectedItem)
  }, [reset, selectedItem])

  useEffect(() => {
    if (selectedItem)
      if (cloneId) {
        reset({
          ...selectedItem,
          timeslots: selectedItem.timeslots?.map((item) => ({
            maxRegistrations: item.maxRegistrations,
            timeRange: {
              from: dayjs(new Date(item.startTime).getTime()).format(
                FORMAT_TIME
              ),
              to: dayjs(new Date(item.endTime).getTime()).format(FORMAT_TIME),
            },
          })),
          // timeslots: [
          //   {
          //     timeRange: {
          //       from: null,
          //       to: null,
          //     },
          //     maxRegistrations: null,
          //   },
          // ],
          startTimeEvent: null,
          receptionBefore: null,
          cancelBefore: null,
          qrValidUntil: null,
        })
      } else
        reset({
          ...selectedItem,
          timeslots: selectedItem.timeslots?.map((item) => ({
            maxRegistrations: item.maxRegistrations,
            _id: item._id,
            timeRange: {
              from: dayjs(new Date(item.startTime).getTime()).format(
                FORMAT_TIME
              ),
              to: dayjs(new Date(item.endTime).getTime()).format(FORMAT_TIME),
            },
          })),
          startTimeEvent: new Date(selectedItem.startTimeEvent),
          receptionBefore: new Date(selectedItem.receptionBefore),
          cancelBefore: new Date(selectedItem.cancelBefore),
          qrValidUntil: (selectedItem as any).qrValidUntil
            ? new Date((selectedItem as any).qrValidUntil)
            : null,
        })
    else
      reset(
        process.env.NODE_ENV === 'development'
          ? buildCreateEventDevMockDefaults()
          : getEmptyCreateEventDefaults()
      )
  }, [cloneId, reset, selectedItem])

  useEffect(() => {
    if (cloneId) fetchDetail(cloneId)
    if (id) fetchDetail(id)

    return () => {
      setSelectedItem(null)
    }
  }, [fetchDetail, id, setSelectedItem, cloneId])

  const onSubmit = async (values: any) => {
    const trimmedRemarksLabel = values.remarksLabel?.trim() ?? ''
    const trimmedRemarksPlaceholder = values.remarksPlaceholder?.trim() ?? ''
    const remarksPayload = {
      remarksLabel: trimmedRemarksLabel,
      remarksPlaceholder: trimmedRemarksPlaceholder,
    }

    try {
      if (id)
        await update(id, {
          ...values,
          ...remarksPayload,
          qrValidUntil: values.qrValidUntil
            ? dayjs(values.qrValidUntil).toISOString()
            : null,
          timeslots: values.timeslots?.map((item) => ({
            maxRegistrations: item.maxRegistrations,
            _id: item._id,
            startTime: dayjs(
              `${dayjs(values.startTimeEvent).format(DATE_TIME_FORMAT.DAY_ONLY)} ${item.timeRange.from}`,
              DATE_TIME_FORMAT.FULL_SECOND
            ).toISOString(),
            endTime: dayjs(
              `${dayjs(values.startTimeEvent).format(DATE_TIME_FORMAT.DAY_ONLY)} ${item.timeRange.to}`,
              DATE_TIME_FORMAT.FULL_SECOND
            ).toISOString(),
          })),
          questions: values.questions.map((e, index) => ({
            ...e,
            order: index,
          })),
        })
      else {
        await create({
          ...values,
          ...remarksPayload,
          qrValidUntil: values.qrValidUntil
            ? dayjs(values.qrValidUntil).toISOString()
            : null,
          timeslots: values.timeslots?.map((item) => ({
            maxRegistrations: item.maxRegistrations,
            startTime: dayjs(
              `${dayjs(values.startTimeEvent).format(DATE_TIME_FORMAT.DAY_ONLY)} ${item.timeRange.from}`,
              DATE_TIME_FORMAT.FULL_SECOND
            ).toISOString(),
            endTime: dayjs(
              `${dayjs(values.startTimeEvent).format(DATE_TIME_FORMAT.DAY_ONLY)} ${item.timeRange.to}`,
              DATE_TIME_FORMAT.FULL_SECOND
            ).toISOString(),
          })),
          questions: values.questions.map((e, index) => ({
            ...e,
            order: index,
          })),
        })
      }
      navigate('/event-management')
    } catch (error) {
      console.log(error)
    }
  }
  const navigate = useNavigate()

  return (
    <div className="card bg-white border-round-xl px-3 py-5">
      <FormProvider {...form}>
        <form
          autoComplete="off"
          onSubmit={handleSubmit(onSubmit)}
          className="p-fluid"
        >
          <EventInfo
            disabled={!!id && selectedItem?.status !== EventStatusEnum.Open}
          />

          <Divider className="border-dashed-divider" />

          <Question
            disabled={!!id && selectedItem?.status !== EventStatusEnum.Open}
          />

          <>
            <Divider className="border-dashed-divider" />
            <RemarkInfo
              disabled={!!id && selectedItem?.status !== EventStatusEnum.Open}
            />
          </>

          <Divider className="border-dashed-divider" />

          <div className="flex gap-4">
            <Button
              loading={loadingSubmit}
              type="submit"
              label={id ? t('Save') : t('Create')}
              className="w-max"
            ></Button>
            <Button
              label={t('Cancel')}
              type="button"
              onClick={() => navigate('/event-management')}
              severity="secondary"
              className="w-min"
            ></Button>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}
export default observer(CreateEvent)
